﻿<script setup lang="ts">
    import { countContacts, anonymizePeriod } from '@cwd/Account/DataManagement.access';
    import { reactive } from 'vue';
    import Loading from '@shared/components/Loading.vue';

    const state = reactive({
        counted: false,
        numberOfContacts: 0,
        inactiveMonths: null,
        confirmAnonymization: false,
        anonymizationStarted: false,
        isLoading: false,
        step: 1,
    });

    async function count() {
        state.isLoading = true;
        if (state.inactiveMonths !== null) state.numberOfContacts = await countContacts(state.inactiveMonths);
        state.counted = true;
        state.isLoading = false;
    }

    async function anonymize() {
        if (state.inactiveMonths === null) {
            throw 'Error, inactiveMonths is null';
        }
        if (!state.confirmAnonymization) {
            throw 'Error, confirmation missing';
        }
        await anonymizePeriod(state.inactiveMonths);
        state.anonymizationStarted = true;
    }

    defineExpose({ state });
</script>
<script lang="ts">
    export default {
        name: 'AnonymizationManager',
    };
</script>

<template>
    <div class="max-700">
        <div v-if="state.step >= 1">
            <p>{{ $Resources.AnonymizationManagerInfoText }}</p>

            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerInactiveContactInfoHeading }}</h4>
            <p v-html="$Resources.AnonymizationManagerInactiveContactInfoText"></p>

            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerDataToDeleteHeading }}</h4>
            <ul v-html="$Resources.AnonymizationManagerDataToDeleteList"></ul>

            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerDataToAnonymizeHeading }}</h4>
            <ul v-html="$Resources.AnonymizationManagerDataToAnonymizeList"></ul>

            <div class="form-group m-xl-t" v-if="state.step == 1">
                <button type="button" class="btn btn-default" @click="state.step++">
                    {{ $Resources.AnonymizationManagerConfirmToContinueBtnText }}
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                </button>
            </div>
        </div>
        <div v-if="state.step == 2">
            <h4 class="m-lg-t">{{ $Resources.AnonymizationManagerSelectTimePeriodHeading }}</h4>

            <div class="btn-group m-lg-b">
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 12 }">
                    <input class="sr-only" value="12" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    12
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 18 }">
                    <input class="sr-only" value="18" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    18
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 24 }">
                    <input class="sr-only" value="24" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    24
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
                <label class="btn btn-default" :class="{ 'btn-primary active': state.inactiveMonths == 36 }">
                    <input class="sr-only" value="36" type="radio" name="inactiveMonths1" v-model="state.inactiveMonths" v-on:change="count" />
                    36
                    {{ $Resources.AnonymizationManagerMonthsLabel }}
                </label>
            </div>

            <div class="panel panel-default">
                <div class="panel-body">
                    <p class="c-light fw-600 m-xs-b">{{ $Resources.AnonymizationManagerNumberOfContactsLabel }}</p>
                    <p class="h1 strong m-0-t m-0-b">
                        <span>{{ state.numberOfContacts }}</span>
                    </p>
                </div>
            </div>

            <div v-if="state.counted == true && state.numberOfContacts === 0" class="alert alert-success alert-fixed-i m-sm-t">
                <p>
                    <i class="fa fa-check" aria-hidden="true"></i>
                    <strong>{{ $Resources.AnonymizationManagerNoContactsToAnonymizeAlertHeading }}</strong>
                    {{ $Resources.AnonymizationManagerNoContactsToAnonymizeAlertText }}
                </p>
            </div>

            <div v-if="state.numberOfContacts > 0">
                <div class="alert alert-danger alert-fixed-i m-sm-b">
                    <p>
                        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                        <strong>{{ $Resources.AnonymizationManagerFinalConfirmAlertHeading }}</strong>
                        {{ $Resources.AnonymizationManagerFinalConfirmAlertTextPart1 }}
                        <strong>{{ state.numberOfContacts }}</strong>
                        {{ $Resources.AnonymizationManagerFinalConfirmAlertTextPart2 }}
                    </p>

                    <div class="form-group">
                        <div class="checkbox slide-toggle">
                            <label>
                                <input type="checkbox" v-model="state.confirmAnonymization" class="form-check-inline" />
                                <span class="toggle"></span>
                                {{ $Resources.AnonymizationManagerFinalConfirmLabel }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="form-group">
                    <button class="btn btn-danger btn-block" :disabled="!state.confirmAnonymization || state.anonymizationStarted" @click="anonymize">
                        {{ $Resources.AnonymizationManagerStartAnonymizationBtnText }}
                    </button>
                </div>
            </div>

            <Loading :show="state.isLoading"></Loading>

            <div v-if="state.anonymizationStarted === true">
                <div class="alert alert-success alert-fixed-i m-sm-t">
                    <p>
                        <i class="fa fa-check" aria-hidden="true"></i>
                        <strong>{{ $Resources.AnonymizationManagerAnonymizationStartedAlertHeading }}</strong>
                        {{ $Resources.AnonymizationManagerAnonymizationStartedAlertText }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
